import office from '@/taskpane/officejs/office-wrapper'
import state from '@/taskpane/state/sidebar-state'
import CallbackArgs, { defaultCallbackArgs } from '../messages/callback-args'
import sendMessageToSidebar from '../messages/send-message-to-sidebar'
import MessageArgs from '../messages/message-args'
import LinkInfo from '../models/link-info'
import CirrusApi from '@/taskpane/services/cirrus-api'

interface ApplyEmailTemplateArgs extends MessageArgs {
  trackEmail: boolean
  trackLinks: boolean
  emailGuid: string
  subject: string
  bodyContent: string
}

interface ApplyEmailTemplateCallbackArgs extends CallbackArgs {
  links: LinkInfo[]
}

export default async function applyEmailTemplateHandler(data: ApplyEmailTemplateArgs) {
  const callbackArgs = defaultCallbackArgs() as ApplyEmailTemplateCallbackArgs
  callbackArgs.links = []

  try {
    const body = await office.body.get(false)
    const bodyWithTemplate = replaceTemplate(body, data.bodyContent)

    if (data.trackEmail) {
      callbackArgs.links = await applyTemplateWithTracking(bodyWithTemplate, data)
    } else {
      await applyTemplateWithoutTracking(bodyWithTemplate)
    }

    await trySetSubject(data.subject)
    await office.save()
  } catch (error) {
    callbackArgs.errorMessage = error || 'Failed to apply email template'
  } finally {
    sendMessageToSidebar('ApplyEmailTemplateCallback', callbackArgs)
  }
}

function replaceTemplate(currentBody: string, newTemplateContent: string): string {
  if (state.currentItem.composeInfo.bodyBeforeTemplate) {
    return insertInBody(newTemplateContent, state.currentItem.composeInfo.bodyBeforeTemplate)
  } else {
    state.currentItem.composeInfo.bodyBeforeTemplate = currentBody
    return insertInBody(newTemplateContent, currentBody)
  }
}

function insertInBody(template: string, prevContent: string): string {
  // Outlook for Desktop returns html document instead of body. parsing separately
  if (prevContent.startsWith('<html') || prevContent.startsWith('<HTML')) {
    const parser = new DOMParser()
    const doc = parser.parseFromString(prevContent, 'text/html')
    const el = doc.createElement('div')
    el.innerHTML = template
    doc.body.insertBefore(el, doc.body.firstChild)
    return doc.documentElement.outerHTML
  } else {
    return template + prevContent
  }
}

async function applyTemplateWithTracking(body: string, data) {
  const rewriteRequest = {
    htmlBodySections: [body],
    emailGuid: data.emailGuid,
    trackEmail: data.trackEmail,
    trackLinks: data.trackLinks,
    linksToChange: [],
  }

  const rewriteResult = await CirrusApi.rewriteEmail(rewriteRequest)

  await office.body.set(rewriteResult.htmlBodySections[0])
  return rewriteResult.links
}

async function applyTemplateWithoutTracking(body: string) {
  await office.body.set(body)
}

async function trySetSubject(newSubject: string) {
  const subject = ((await office.subject.get()) || '').toLowerCase()
  if (subject.indexOf('re') && subject.indexOf('fwd') && subject.indexOf('fw')) {
    await office.subject.set(newSubject)
  }
}
